/**
 * The IIT Bombay × IRCC "Register interest" form: name, e-mail, department,
 * 1-N technologies (a title and a TRL each). No payment terms or consent
 * tick (owner, 2026-10-15): nothing is paid here, IRCC follows up.
 *
 * POST /api/v1/ip-intel/programme/iitb_ircc/register stores it and e-mails
 * the faculty member a confirmation (and the team a notice). The server
 * re-checks every field and answers a bad one with `field`, shown inline;
 * a 409 means the programme closed, fewer places are left than asked for
 * (only while places are limited), or (when the programme allows one form
 * per address) the address has registered already; the page's config is
 * refreshed so the stepper and the closed state follow. With places
 * unlimited the stepper goes up to the per-form maximum and no places count
 * is shown. The e-mail domain hint and check apply only
 * when the programme lists allowed domains (none = any address). A draft
 * is kept for the tab, and a signed-in visitor's name and e-mail are
 * filled in.
 *
 * Styles: the .ipr scope (ipintel/styles.tsx) plus the page's .ircc-* rules.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { EMAIL_RE } from './content';
import {
  IRCC_DRAFT_KEY, IRCC_FALLBACK, IRCC_TRLS, closesOn, emailDomainAllowed, emailDomainsHint, placesLimited,
  shareTotalLabel, techWord,
} from './irccContent';
import { irccApi, IrccProgramme, IrccTechInput } from './irccApi';
import { ipClip, ipTrack } from './track';

const NAME_MAX = 120;
const TITLE_MIN = 3;
const TITLE_MAX = 300;
const EMPTY_TECH: IrccTechInput = { title: '', trl: '' };

interface Draft { name: string; email: string; dept: string; techs: IrccTechInput[] }

const readDraft = (): Draft | null => {
  try {
    const v = JSON.parse(sessionStorage.getItem(IRCC_DRAFT_KEY) || 'null');
    if (!v || typeof v !== 'object') return null;
    const techs = Array.isArray(v.techs) && v.techs.length
      ? v.techs.slice(0, 50).map((t: any) => ({ title: String(t?.title || ''), trl: String(t?.trl || '') }))
      : [{ ...EMPTY_TECH }];
    return { name: String(v.name || ''), email: String(v.email || ''), dept: String(v.dept || ''), techs };
  } catch { return null; }
};

/** Element id for a server `field`, so it can be focused. */
const fieldId = (field: string): string => {
  const m = /^technologies\.(\d+)\.(title|trl)$/.exec(field);
  if (m) return `ircc-tech-${m[1]}-${m[2]}`;
  return ({ name: 'ircc-name', email: 'ircc-email', department: 'ircc-dept', technologies: 'ircc-count-dec', agree: 'ircc-agree' } as Record<string, string>)[field] || '';
};

interface Props {
  /** null until the config arrives (or when it failed). */
  programme: IrccProgramme | null;
  configState: 'loading' | 'ready' | 'error';
  onRetry: () => void;
  /** Re-read the config (after a registration, or a 409 that says it changed). */
  onRefresh: () => void;
}

interface Done { ref: string; count: number; email: string; emailed: boolean; shareTotal: string }

export const RegisterInterest: React.FC<Props> = ({ programme, configState, onRetry, onRefresh }) => {
  const initial = useMemo(() => readDraft(), []);
  const [name, setName] = useState(initial?.name || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [dept, setDept] = useState(initial?.dept || '');
  const [techs, setTechs] = useState<IrccTechInput[]>(initial?.techs || [{ ...EMPTY_TECH }]);
  const [hp, setHp] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [phase, setPhase] = useState<'form' | 'sending' | 'done'>('form');
  const [done, setDone] = useState<Done | null>(null);
  const sendingRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const signedInId = apiService.getStoredUserId?.() || '';

  const shareMinor = programme?.share_minor ?? IRCC_FALLBACK.share_minor;
  const tax = programme ? programme.tax_label : IRCC_FALLBACK.tax_label;
  const contact = programme?.contact_email || IRCC_FALLBACK.contact_email;
  const days = programme?.turnaround_days || IRCC_FALLBACK.turnaround_days;
  // Empty = any address (no hint, no check); the server re-checks either way.
  const domains = useMemo(() => (programme?.allowed_email_domains || [])
    .map(d => String(d || '').trim().toLowerCase().replace(/^@/, '')).filter(Boolean),
  [programme?.allowed_email_domains]);
  const domainsHint = emailDomainsHint(domains);
  const perForm = programme?.max_per_registration || IRCC_FALLBACK.max_per_registration;
  // Unlimited places: only the per-form maximum bounds the stepper.
  const limited = placesLimited(programme);
  const remaining = programme && limited ? Number(programme.remaining ?? 0) : perForm;
  const maxTechs = Math.max(1, Math.min(perForm, remaining));
  const count = techs.length;
  const shareTotal = shareTotalLabel(shareMinor, count, tax);

  // Prefill from the signed-in profile, only into fields still empty.
  useEffect(() => {
    if (!signedInId) return;
    let live = true;
    apiService.getProfile(signedInId).then(res => {
      if (!live || !res.success || !res.data) return;
      const u: any = (res.data as any).user || {};
      const full = [u.first_name, u.last_name].map((x: any) => String(x || '').trim()).filter(Boolean).join(' ');
      setName(prev => prev || full);
      setEmail(prev => prev || String(u.email || ''));
    }).catch(() => { /* prefill is a convenience */ });
    return () => { live = false; };
  }, [signedInId]);

  useEffect(() => {
    if (phase === 'done') return;
    try { sessionStorage.setItem(IRCC_DRAFT_KEY, JSON.stringify({ name, email, dept, techs })); } catch { /* ignore */ }
  }, [name, email, dept, techs, phase]);

  const clearErr = (...keys: string[]) => {
    if (keys.some(k => errors[k])) setErrors(prev => { const n = { ...prev }; keys.forEach(k => delete n[k]); return n; });
    if (formError) setFormError('');
  };

  const setTech = (i: number, key: keyof IrccTechInput, value: string) => {
    setTechs(prev => prev.map((t, j) => (j === i ? { ...t, [key]: value } : t)));
    clearErr(`technologies.${i}.${key}`);
  };

  const inc = () => {
    if (count >= maxTechs) return;
    setTechs(prev => [...prev, { ...EMPTY_TECH }]);
    clearErr('technologies');
    ipTrack('ipintel_ircc_tech_count', { count: count + 1, via: 'inc' });
  };
  const dec = () => {
    if (count <= 1) return;
    setTechs(prev => prev.slice(0, prev.length - 1));
    clearErr('technologies', `technologies.${count - 1}.title`, `technologies.${count - 1}.trl`);
    ipTrack('ipintel_ircc_tech_count', { count: count - 1, via: 'dec' });
  };

  // What still stands between the visitor and the button (the prototype's hint line).
  const titlesFolded = techs.map(t => t.title.trim().replace(/\s+/g, ' ').toLowerCase());
  const dupAt = titlesFolded.findIndex((t, i) => t && titlesFolded.indexOf(t) !== i);
  const detailsDone = !!(name.trim() && email.trim() && dept.trim());
  const emailOk = EMAIL_RE.test(email.trim());
  const domainOk = emailDomainAllowed(email, domains);
  const techsFilled = techs.every(t => t.title.trim() && t.trl);
  const titlesLongEnough = techs.every(t => t.title.trim().length >= TITLE_MIN);
  const tooMany = count > maxTechs;
  let hint = 'Ready to register.';
  if (!detailsDone) hint = 'Fill in your name, email and department.';
  else if (!emailOk) hint = 'Check your e-mail address.';
  else if (!domainOk) hint = domainsHint;
  else if (!techsFilled) hint = 'Add a title and TRL for each technology.';
  else if (!titlesLongEnough) hint = `Each title needs at least ${TITLE_MIN} characters.`;
  else if (dupAt >= 0) hint = 'Two technologies have the same title.';
  else if (tooMany) hint = `Remove technologies to ${maxTechs} or fewer.`;
  const ready = hint === 'Ready to register.';
  const busy = phase === 'sending';

  const focusField = (field: string) => setTimeout(() => {
    const id = fieldId(field);
    if (id) document.getElementById(id)?.focus();
  }, 0);

  const submit = async () => {
    if (sendingRef.current || !ready) return;
    sendingRef.current = true;
    setPhase('sending');
    setErrors({});
    setFormError('');
    const body = {
      name: name.trim(),
      email: email.trim().toLowerCase(),
      department: dept.trim(),
      technologies: techs.map(t => ({ title: t.title.trim(), trl: Number(t.trl) })),
      // No payment terms or tick box on the page any more (owner, 2026-10-15):
      // terms_shown=false tells the server not to ask for or record consent.
      // agree stays for a server that predates that.
      agree: true,
      terms_shown: false,
      website: hp,
      ...(signedInId ? { user_id: signedInId } : {}),
    };
    ipTrack('ipintel_ircc_submit', { count, signed_in: !!signedInId });
    const res = await irccApi.register(body);
    sendingRef.current = false;
    if (res.ok) {
      ipTrack('ipintel_ircc_success', { ref: res.data.ref, count: res.data.count });
      try { sessionStorage.removeItem(IRCC_DRAFT_KEY); } catch { /* ignore */ }
      setDone({
        ref: res.data.ref, count: res.data.count, email: res.data.email || body.email,
        emailed: res.data.emailed !== false, shareTotal: res.data.share_total_label || shareTotal,
      });
      setPhase('done');
      onRefresh();
      setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      return;
    }
    setPhase('form');
    ipTrack('ipintel_ircc_error', {
      stage: 'server', status: res.status, field: res.field || null, error: ipClip(res.error, 120),
    });
    if (res.error === 'rate_limited') {
      setFormError(`Too many registrations from your network just now. Please wait a little and try again, or write to ${contact}.`);
      return;
    }
    if (res.status === 409) onRefresh();
    if (res.field) {
      setErrors({ [res.field]: res.error });
      focusField(res.field);
      return;
    }
    setFormError(res.status >= 500 || res.status === 0
      ? `${res.status === 0 ? res.error : 'Something went wrong on our side and your registration was not saved.'} Please try again in a moment, or write to ${contact}.`
      : res.error);
  };

  const reset = () => {
    ipTrack('ipintel_ircc_reset', { ref: done?.ref || null });
    setName(''); setEmail(''); setDept(''); setTechs([{ ...EMPTY_TECH }]); setHp('');
    setErrors({}); setFormError(''); setDone(null); setPhase('form');
    setTimeout(() => document.getElementById('ircc-name')?.focus(), 0);
  };

  const err = (k: string) => errors[k] ? <div id={`${fieldId(k) || k}-err`} className="ipr-field-error" role="alert">{errors[k]}</div> : null;
  const described = (k: string) => (errors[k] ? `${fieldId(k) || k}-err` : undefined);

  // ---- success ------------------------------------------------------------
  if (phase === 'done' && done) {
    return (
      <div ref={cardRef} className="ircc-card" role="status">
        <span className="ircc-check" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--ipr-primary-700)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
        </span>
        <h3 className="ircc-h3">Thank you, your interest is registered</h3>
        <p className="ircc-p">
          You registered {done.count} {techWord(done.count)} ({done.shareTotal} in all). Your reference
          is <strong>{done.ref}</strong>.{' '}
          {done.emailed
            ? <>We've sent a confirmation to <strong style={{ overflowWrap: 'anywhere' }}>{done.email}</strong>.</>
            : <>We couldn't send the confirmation e-mail just now, but your registration is saved.</>}
        </p>
        <p className="ircc-p" style={{ margin: 0 }}>Here is what happens next:</p>
        <ol className="ircc-next">
          <li>IRCC team will get in touch with you with next steps.</li>
          <li>UniversityTech.io will email you to confirm your technology details.</li>
          <li>Your report arrives within {days} days of your confirmation.</li>
        </ol>
        <button type="button" className="ipr-btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={reset}>
          Register another response
        </button>
      </div>
    );
  }

  // ---- config failed / loading ---------------------------------------------
  if (configState === 'error' && !programme) {
    return (
      <div ref={cardRef} className="ircc-card">
        <h3 className="ircc-h3">Registration is temporarily unavailable</h3>
        <p className="ircc-p">We couldn't load the form just now. Please try again in a moment, or write to {contact}.</p>
        <button type="button" className="ipr-btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={onRetry}>Try again</button>
      </div>
    );
  }
  if (!programme) {
    return (
      <div ref={cardRef} className="ircc-card" aria-busy="true" aria-label="Loading the form">
        <span className="ipr-skel" style={{ width: '60%', height: 18 }} />
        <span className="ipr-skel" style={{ width: '100%', height: 48 }} />
        <span className="ipr-skel" style={{ width: '100%', height: 48 }} />
        <span className="ipr-skel" style={{ width: '40%', height: 48 }} />
      </div>
    );
  }

  // ---- closed --------------------------------------------------------------
  if (!programme.open) {
    // 'full' only happens while places are limited; otherwise no count is named.
    const full = limited && programme.closed_reason === 'full';
    const when = closesOn(programme.closes_at);
    return (
      <div ref={cardRef} className="ircc-card">
        <h3 className="ircc-h3">{full ? `All ${programme.tech_cap} places are taken` : 'Registrations have closed'}</h3>
        <p className="ircc-p">
          {full
            ? `All ${programme.tech_cap} technologies for this round are in, so registrations have closed.`
            : programme.closed_reason === 'deadline' && when
              ? `Registrations closed on ${when}.`
              : 'Registrations are closed for now.'}
          {' '}For any question, write to <a href={`mailto:${contact}`} className="ircc-link">{contact}</a>.
        </p>
      </div>
    );
  }

  // ---- form ----------------------------------------------------------------
  return (
    <div ref={cardRef} className="ircc-card">
      <form noValidate onSubmit={e => { e.preventDefault(); void submit(); }} className="ircc-form">
        <div className="ircc-grid-2">
          <div>
            <label className="ipr-label" htmlFor="ircc-name">Full name</label>
            <input id="ircc-name" type="text" className={`ipr-input ircc-input${errors.name ? ' is-invalid' : ''}`}
              value={name} maxLength={NAME_MAX} autoComplete="name" placeholder="Prof. A. Sharma" disabled={busy}
              aria-invalid={!!errors.name} aria-describedby={described('name')}
              onChange={e => { setName(e.target.value); clearErr('name'); }} />
            {err('name')}
          </div>
          <div>
            <label className="ipr-label" htmlFor="ircc-email">Email</label>
            <input id="ircc-email" type="email" className={`ipr-input ircc-input${errors.email ? ' is-invalid' : ''}`}
              value={email} maxLength={254} autoComplete="email" placeholder="name@iitb.ac.in" disabled={busy}
              aria-invalid={!!errors.email}
              aria-describedby={[described('email'), domainsHint ? 'ircc-email-hint' : ''].filter(Boolean).join(' ') || undefined}
              onChange={e => { setEmail(e.target.value); clearErr('email'); }} />
            {err('email')}
            {domainsHint && !errors.email && <div id="ircc-email-hint" className="ipr-hint">{domainsHint}</div>}
          </div>
        </div>

        <div className="ircc-grid-2" style={{ alignItems: 'end' }}>
          <div>
            <label className="ipr-label" htmlFor="ircc-dept">Department</label>
            <input id="ircc-dept" type="text" className={`ipr-input ircc-input${errors.department ? ' is-invalid' : ''}`}
              value={dept} maxLength={NAME_MAX} autoComplete="organization-title" placeholder="e.g. Chemical Engineering"
              disabled={busy} aria-invalid={!!errors.department} aria-describedby={described('department')}
              onChange={e => { setDept(e.target.value); clearErr('department'); }} />
            {err('department')}
          </div>
          <div>
            <span id="ircc-count-label" className="ipr-label">Number of technologies</span>
            <div role="group" aria-labelledby="ircc-count-label" className="ircc-stepper">
              <button id="ircc-count-dec" type="button" onClick={dec} disabled={busy || count <= 1} aria-label="Remove a technology"
                aria-describedby={described('technologies')}>−</button>
              <span aria-live="polite">{count}</span>
              <button type="button" onClick={inc} disabled={busy || count >= maxTechs} aria-label="Add a technology">+</button>
            </div>
            {limited && remaining < perForm && (
              <div className="ipr-hint">{remaining} of {programme.tech_cap} places left</div>
            )}
          </div>
        </div>
        {err('technologies')}

        <div className="ircc-techs">
          {techs.map((t, i) => (
            <fieldset key={i} className="ircc-tech">
              <legend>Technology {i + 1}</legend>
              <div className="ircc-tech-title">
                <label className="ipr-label" htmlFor={`ircc-tech-${i}-title`}>Title</label>
                <input id={`ircc-tech-${i}-title`} type="text"
                  className={`ipr-input ircc-input${errors[`technologies.${i}.title`] ? ' is-invalid' : ''}`}
                  value={t.title} maxLength={TITLE_MAX} placeholder="As it appears in your patent or disclosure"
                  disabled={busy} aria-invalid={!!errors[`technologies.${i}.title`]}
                  aria-describedby={described(`technologies.${i}.title`)}
                  onChange={e => setTech(i, 'title', e.target.value)} />
                {err(`technologies.${i}.title`)}
                {dupAt === i && !errors[`technologies.${i}.title`] && (
                  <div className="ipr-hint">Same title as an earlier technology.</div>
                )}
              </div>
              <div>
                <label className="ipr-label" htmlFor={`ircc-tech-${i}-trl`}>TRL</label>
                <select id={`ircc-tech-${i}-trl`}
                  className={`ipr-input ircc-input${errors[`technologies.${i}.trl`] ? ' is-invalid' : ''}`}
                  value={t.trl} disabled={busy} aria-invalid={!!errors[`technologies.${i}.trl`]}
                  aria-describedby={described(`technologies.${i}.trl`)}
                  onChange={e => setTech(i, 'trl', e.target.value)}>
                  <option value="">Select TRL</option>
                  {IRCC_TRLS.map(l => <option key={l.n} value={String(l.n)}>TRL {l.n} – {l.label}</option>)}
                </select>
                {err(`technologies.${i}.trl`)}
              </div>
            </fieldset>
          ))}
        </div>

        {err('agree')}

        {/* Honeypot: invisible to people, irresistible to form-filling bots. */}
        <div className="ipr-hp" aria-hidden="true">
          <label htmlFor="ircc-website">Website</label>
          <input id="ircc-website" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} />
        </div>

        {formError && <div className="ipr-alert ipr-alert-error" role="alert">{formError}</div>}

        <div className="ircc-submit-row">
          <button type="submit" className="ipr-btn-primary" disabled={!ready || busy}>
            {busy && <span className="ipr-spin" aria-hidden="true" />}
            {busy ? 'Registering…' : 'Register interest'}
          </button>
          <span className="ircc-hint" aria-live="polite">{busy ? 'Sending your registration…' : hint}</span>
        </div>
      </form>
    </div>
  );
};
